import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView, useScroll, useTransform } from "framer-motion";
import { Play } from "lucide-react";
import { useLang, WHATSAPP_URL } from "../i18n";
import { WhatsAppIcon } from "./Logo";
import VoiceWave from "./VoiceWave";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} className="font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">
      {Math.round(val).toLocaleString("de-CH")}
      <span className="text-gradient">{suffix}</span>
    </span>
  );
};

export const Hero = () => {
  const { t } = useLang();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const waveY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 200]);

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="glow-radar noise relative flex min-h-screen flex-col justify-center overflow-hidden pt-28 pb-16"
    >
      <motion.div style={{ y: glowY }} className="grid-lines absolute inset-0 opacity-70" />
      <video
        autoPlay
        muted
        loop
        playsInline
        data-testid="hero-wave-video"
        className="absolute inset-0 h-full w-full object-cover opacity-45"
      >
        <source src="/video/hero-wave.webm" type="video/webm" />
        <source src="/video/hero-wave.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-[#070B11]/70 via-[#070B11]/30 to-[#070B11]" />
      <div className="absolute -top-32 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-[120px]" />

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5"
          data-testid="hero-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          <span className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-emerald-300">
            {t.hero.badge}
          </span>
        </motion.div>

        <h1 className="font-display max-w-5xl text-4xl font-black leading-[1.06] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-gradient" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#ai-demo"
            data-testid="hero-try-ai-btn"
            className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 px-7 py-4 text-base font-bold text-slate-950 shadow-[0_0_40px_rgba(16,185,129,0.4)] transition-all hover:scale-[1.04] hover:shadow-[0_0_60px_rgba(16,185,129,0.55)]"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-950/15 transition-transform group-hover:scale-110">
              <Play className="h-4 w-4 fill-current" />
            </span>
            {t.hero.cta1}
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="glass inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-base font-semibold text-slate-100 transition-all hover:scale-[1.04] hover:border-emerald-500/40"
          >
            <WhatsAppIcon className="h-5 w-5 text-emerald-400" />
            {t.hero.cta2}
          </a>
        </motion.div>

        <motion.div
          style={{ y: waveY }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          className="glass relative mt-16 overflow-hidden rounded-3xl p-5 sm:p-7"
          data-testid="hero-wave-panel"
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
              <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-emerald-300">
                {t.hero.live}
              </span>
            </div>
            <span className="font-mono2 text-[11px] tracking-widest text-slate-500">IT · DE · FR · EN</span>
          </div>
          <VoiceWave className="h-28 w-full sm:h-36" />
        </motion.div>

        <div className="mt-14 grid grid-cols-2 gap-8 lg:grid-cols-4" data-testid="hero-stats">
          {t.hero.stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="border-l border-slate-800 pl-5"
            >
              <Counter value={s.value} suffix={s.suffix} />
              <p className="mt-2 text-sm text-slate-500">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
